import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import EditIcon from '@mui/icons-material/Edit'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { CASE_CLIENT_ROLE_LABELS, CASE_TYPE_LABELS, type ProjectDetail } from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ContactsPanel } from '../components/contacts/ContactsPanel'
import { ProjectDialog } from '../components/projects/ProjectDialog'
import { TaskDialog } from '../components/tasks/TaskDialog'
import { TasksTable } from '../components/tasks/TasksTable'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  )
}

export function ProjectDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.project(id),
    queryFn: () => api.get<ProjectDetail>(`/projects/${id}`),
    enabled: Boolean(id),
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.project(id) })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    // A change here propagates to every case that inherits from this project.
    void queryClient.invalidateQueries({ queryKey: ['case'] })
  }

  const attachContact = useMutation({
    mutationFn: (vars: { contactId: string; role: string }) =>
      api.post(`/projects/${id}/contacts`, vars),
    onSuccess: invalidate,
  })

  const changeRole = useMutation({
    mutationFn: (vars: { contactId: string; role: string }) =>
      api.patch(`/projects/${id}/contacts/${vars.contactId}`, { role: vars.role }),
    onSuccess: invalidate,
  })

  const detachContact = useMutation({
    mutationFn: (contactId: string) => api.del(`/projects/${id}/contacts/${contactId}`),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/projects/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
  })

  if (isPending) return <LoadingBlock />
  if (error) return <ErrorAlert error={error} onRetry={() => void refetch()} />
  if (!data) return <EmptyState title="הפרויקט לא נמצא" />

  const address = [data.street, data.city].filter(Boolean).join(', ')
  const block = [data.gush && `גוש ${data.gush}`, data.parcel && `חלקה ${data.parcel}`]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <PageHeader
        title={data.displayName}
        subtitle={[address, block].filter(Boolean).join(' · ')}
        actions={
          <>
            <IconButton onClick={() => navigate('/projects')} title="חזרה לרשימה">
              <ArrowForwardIcon />
            </IconButton>
            <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              עריכה
            </Button>
            <Button color="error" onClick={() => setConfirmDelete(true)}>
              מחיקה
            </Button>
          </>
        }
      />

      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      <Tabs value={tab} onChange={(_e, next) => setTab(next)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="פרטים" />
        <Tab label={`אנשי קשר (${data.contacts.length})`} />
        <Tab label={`תיקים (${data.cases.length})`} />
        <Tab label="משימות" />
      </Tabs>

      {tab === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Grid container spacing={2.5}>
            <DetailRow label="שם בעברית" value={data.hebrewName} />
            <DetailRow label="שם באנגלית" value={data.name} />
            <DetailRow label="חברה יזמית" value={data.developerName} />
            <DetailRow label="גוש" value={data.gush} />
            <DetailRow label="חלקה" value={data.parcel} />
            <DetailRow label="עיר" value={data.city} />
            <DetailRow label="רחוב" value={data.street} />
          </Grid>
        </Paper>
      ) : null}

      {tab === 1 ? (
        <ContactsPanel
          contacts={data.contacts}
          busy={attachContact.isPending || detachContact.isPending || changeRole.isPending}
          error={attachContact.error ?? detachContact.error ?? changeRole.error}
          onAttach={(contactId, role) => attachContact.mutate({ contactId, role })}
          onRoleChange={(contactId, role) => changeRole.mutate({ contactId, role })}
          onDetach={(contactId) => detachContact.mutate(contactId)}
        />
      ) : null}

      {tab === 2 ? (
        data.cases.length === 0 ? (
          <EmptyState title="אין תיקים בפרויקט" />
        ) : (
          <Stack spacing={1}>
            {data.cases.map((row) => (
              <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/cases/${row.id}`)}
                    sx={{ font: 'inherit', flexGrow: 1, textAlign: 'right' }}
                  >
                    {row.name}
                  </Link>
                  <Chip size="small" label={CASE_TYPE_LABELS[row.caseType]} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={CASE_CLIENT_ROLE_LABELS[row.clientRole]}
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        )
      ) : null}

      {tab === 3 ? (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button startIcon={<AddIcon />} onClick={() => setTaskOpen(true)}>
              משימה חדשה לפרויקט
            </Button>
          </Stack>
          <TasksTable
            query={{ projectId: id, pageSize: 200 }}
            onOpenTask={setOpenTaskId}
            hiddenColumns={['projectName']}
            height={520}
          />
        </>
      ) : null}

      <ProjectDialog open={editOpen} project={data} onClose={() => setEditOpen(false)} />
      <TaskDialog open={taskOpen} defaults={{ projectId: id }} onClose={() => setTaskOpen(false)} />
      <TaskDialog open={Boolean(openTaskId)} taskId={openTaskId} onClose={() => setOpenTaskId(null)} />

      <ConfirmDialog
        open={confirmDelete}
        title="מחיקת פרויקט"
        message="הפרויקט יימחק. מחיקה אפשרית רק כשאין תיקים משויכים אליו."
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}
